import { AlertTriangle, BookOpenCheck, BrainCircuit, CheckCircle2, Circle, GitCommitHorizontal, Lightbulb, UserRound } from "lucide-react";

import type { PendingChange } from "../api/client";
import { useStoryPreferences } from "../i18n";

type InspectorPanelProps = {
  status: "pending" | "accepted" | "rejected" | "empty";
  pendingChange?: PendingChange;
  currentContent: string[];
  facts?: string[];
  invalidationCount?: number;
  eventCount?: number;
  relationshipCount?: number;
  busy?: boolean;
  error?: string | null;
  onAccept: () => void;
  onReject: () => void;
  onOpenWorld?: () => void;
  onOpenContext?: () => void;
  overlay?: boolean;
};

type DiffRow = { index: number; before: string; after: string };

function paragraphs(content: string): string[] {
  return content.split(/\n\s*\n/u).map((value) => value.trim()).filter(Boolean);
}

function diffRows(current: string[], proposed: string): DiffRow[] {
  const next = paragraphs(proposed);
  const count = Math.max(current.length, next.length);
  const result: DiffRow[] = [];
  for (let index = 0; index < count; index += 1) {
    const before = current[index] ?? "";
    const after = next[index] ?? "";
    if (before !== after) result.push({ index, before, after });
    if (result.length === 4) break;
  }
  return result;
}

export function InspectorPanel({
  status,
  pendingChange,
  currentContent,
  facts = [],
  invalidationCount = 0,
  eventCount = 0,
  relationshipCount = 0,
  busy = false,
  error = null,
  onAccept,
  onReject,
  onOpenWorld,
  onOpenContext,
  overlay = false,
}: InspectorPanelProps) {
  const { t } = useStoryPreferences();
  const rows = pendingChange ? diffRows(currentContent, pendingChange.proposedContent) : [];
  const decisionCopy = status === "accepted" ? t("acceptedChange") : status === "rejected" ? t("rejectedChange") : t("noReview");
  return (
    <aside className={`inspector-panel story-assistant${overlay ? " mobile-overlay-panel" : ""}`} aria-label={t("reviewWorkspace")}>
      <header className="assistant-header"><div><GitCommitHorizontal /><strong>{t("reviewWorkspace")}</strong></div></header>
      <section className="assistant-section context-summary">
        <h2>{t("currentContext")}</h2>
        <dl><div><dt>{t("canonFacts")}</dt><dd>{facts.length}</dd></div><div><dt>{t("timelineEventsCount")}</dt><dd>{eventCount}</dd></div><div><dt>{t("relationships")}</dt><dd>{relationshipCount}</dd></div><div><dt>{t("conflictsCount")}</dt><dd>{invalidationCount}</dd></div><div><dt>{t("draftWords")}</dt><dd>{currentContent.join("").length}</dd></div></dl>
      </section>
      <section className="assistant-section real-workspace-actions"><h2>{t("connectedTools")}</h2><div>{onOpenContext ? <button type="button" onClick={onOpenContext}><BrainCircuit />{t("contextAi")}</button> : null}<button type="button" onClick={onOpenWorld}><BookOpenCheck />{t("world")}</button></div><p><Lightbulb />{t("realToolsHint")}</p></section>
      <section className="change-section assistant-review">
        <div className="change-title-row">
          <div>
            <h2>{status === "pending" ? t("pendingChange") : t("reviewChanges")}</h2>
            <p>{pendingChange ? pendingChange.baseCommitId.slice(0, 12) : t("draftCanon")}</p>
          </div>
        </div>
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
        {status === "pending" && pendingChange ? (
          <>
            <div className="semantic-diff" aria-label="语义差异">
              {rows.length > 0 ? rows.map((row) => (
                <div className="diff-group" key={row.index}>
                  <div className="diff-location">¶ {row.index + 1}</div>
                  {row.before ? <div className="diff-line removed"><span>−</span><p>{row.before}</p></div> : null}
                  {row.after ? <div className="diff-line added"><span>+</span><p>{row.after}</p></div> : null}
                </div>
              )) : <p className="empty-diff">NarrativeDelta only</p>}
            </div>
            <div className="change-actions">
              <button type="button" className="secondary-button" disabled={busy} onClick={onReject}>{t("reject")}</button>
              <button type="button" className="primary-button" disabled={busy} onClick={onAccept}>{busy ? t("submitting") : t("accept")}</button>
            </div>
          </>
        ) : (
          <div className={`decision-state ${status}`}>
            {status === "accepted" ? <CheckCircle2 /> : status === "empty" ? <Circle /> : <Circle />}
            <p>{decisionCopy}</p>
          </div>
        )}
      </section>
      <section className="canon-section">
        <h3>{t("canonFacts")}</h3>
        {facts.length > 0 ? facts.slice(0, 3).map((fact) => (
          <div className="canon-row" key={fact}><UserRound size={17} /><span>{fact}</span><i className="fact-state green" /></div>
        )) : <div className="canon-row muted-row"><Circle size={17} /><span>{t("noFacts")}</span></div>}
        {invalidationCount > 0 ? (
          <div className="canon-row invalidation-row"><AlertTriangle size={17} /><span>{invalidationCount} {t("conflictsCount")}</span><i className="fact-state red" /></div>
        ) : null}
        <button type="button" className="all-facts" onClick={onOpenWorld}>{t("allFacts")} <GitCommitHorizontal size={17} /></button>
      </section>
    </aside>
  );
}
